import { useEffect, useMemo, useRef, useState } from "react";
import PhoneInput, { getCountryCallingCode } from "react-phone-number-input";
import { getExampleNumber } from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";
import "react-phone-number-input/style.css";
import { useTranslation } from "react-i18next";

const DEFAULT_COUNTRY = "DK";
const DEFAULT_PLACEHOLDER = "+45 12 34 56 78";

const CustomCountrySelect = ({
  value,
  onChange,
  options,
  iconComponent: Icon,
  disabled,
  readOnly,
  onFocus,
  onBlur,
  className,
  "aria-label": ariaLabel,
  menuPosition = "bottom",
}) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const selectedOption = useMemo(
    () => options.find((option) => !option.divider && option.value === value),
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const selectable = options.filter((option) => !option.divider);

    if (!query) return selectable;

    return selectable.filter(({ value: countryCode, label }) => {
      const callingCode = countryCode ? getCountryCallingCode(countryCode) : "";
      const tokens = [
        label?.toLowerCase(),
        countryCode?.toLowerCase(),
        callingCode,
        `+${callingCode}`,
      ].filter(Boolean);

      return tokens.some((token) => token.includes(query));
    });
  }, [options, searchQuery]);

  const isDisabled = disabled || readOnly;

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (containerRef.current?.contains(event.target)) return;
      setIsOpen(false);
      setSearchQuery("");
      onBlur?.();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onBlur]);

  useEffect(() => {
    if (isOpen) searchRef.current?.focus();
  }, [isOpen]);

  const closeDropdown = () => {
    setIsOpen(false);
    setSearchQuery("");
    onBlur?.();
  };

  const openDropdown = () => {
    if (isDisabled) return;
    setIsOpen(true);
    onFocus?.();
  };

  const handleSelect = (countryCode) => {
    onChange(countryCode);
    closeDropdown();
  };

  const menuPositionClass = useMemo(() => {
    switch (menuPosition) {
      case "top":
        return "bottom-full translate-y-[-5%] flex flex-col-reverse";
      default:
        return "top-full mt-3";
    }
  }, [menuPosition]);

  return (
    <div ref={containerRef} className={`PhoneInputCountry relative ${className ?? ""}`}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        disabled={isDisabled}
        onClick={() => (isOpen ? closeDropdown() : openDropdown())}
        className="flex items-center gap-1 border-0 bg-transparent p-0 cursor-pointer disabled:cursor-not-allowed"
      >
        {Icon && (
          <Icon aria-hidden country={value} label={selectedOption?.label ?? "International"} />
        )}
        <div className="PhoneInputCountrySelectArrow" />
      </button>

      {isOpen && (
        <div
          className={`absolute -left-4 z-50 mt-1 w-72 rounded-md border border-[#EDF1F2] bg-white shadow-lg ${menuPositionClass}`}
        >
          <div className="border-b border-[#EDF1F2] p-2">
            <input
              ref={searchRef}
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={t("common.searchCountryOrCode")}
              className="w-full rounded-md bg-[#EFF5F5] px-3 py-2 text-sm text-[#303030] focus:outline-none"
            />
          </div>

          <ul role="listbox" className="max-h-56 overflow-y-auto py-1" aria-label={ariaLabel}>
            {filteredOptions.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-500">{t("common.noCountriesFound")}</li>
            ) : (
              filteredOptions.map((option) => {
                const callingCode = option.value && getCountryCallingCode(option.value);
                return (
                  <li
                    key={option.value ?? "ZZ"}
                    role="option"
                    aria-selected={option.value === value}
                  >
                    <button
                      type="button"
                      onClick={() => handleSelect(option.value)}
                      className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-black hover:bg-[#EFF5F5] ${
                        option.value === value ? "bg-[#EFF5F5] font-medium" : ""
                      }`}
                    >
                      {Icon && <Icon aria-hidden country={option.value} label={option.label} />}
                      <span className="text-xs text-gray-500">
                        {callingCode ? `(+${callingCode})` : t("common.none")}
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const getPhonePlaceholder = (country = DEFAULT_COUNTRY) => {
  try {
    return getExampleNumber(country, examples)?.formatInternational() ?? DEFAULT_PLACEHOLDER;
  } catch {
    return DEFAULT_PLACEHOLDER;
  }
};

const CustomPhoneInput = ({ onChange, value, name, menuPosition = "bottom", ...props }) => {
  const [placeholder, setPlaceholder] = useState(() => getPhonePlaceholder());

  const handleCountryChange = (country) => {
    if (country) setPlaceholder(getPhonePlaceholder(country));
  };

  return (
    <PhoneInput
      placeholder={placeholder}
      value={value}
      defaultCountry={DEFAULT_COUNTRY}
      onChange={(phone) => onChange({ target: { name, value: phone ?? "" } })}
      onCountryChange={handleCountryChange}
      countrySelectComponent={CustomCountrySelect}
      countrySelectProps={{ menuPosition }}
      {...props}
    />
  );
};

export default CustomPhoneInput;
